import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import ComptesPayables from "./ComptesPayables";
import { dayKeyMtl } from "../../lib/dateMtl";
import { RangePicker } from "./RangePicker";
import { toast } from "sonner";
import { Loader2, FileBarChart, ChevronRight, ChevronDown, Printer, FileSpreadsheet, Wand2, FlaskConical, Lock, LockOpen, TrendingDown, Send } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

// Catégories de vente (même liste que le backend — statistiques à long terme)
const CATEGORIES = [["resine", "Résine"], ["verre", "Verre"], ["cristal", "Cristal"],
                    ["acrylique", "Acrylique"], ["medailles_serie", "Médailles de série"],
                    ["medailles_sur_mesure", "Médailles sur mesure"], ["plaques", "Plaques"],
                    ["gravure", "Gravure seulement"], ["impression", "Impression seulement"],
                    ["promotionnel", "Promotionnel"], ["autres", "Autres"]];

// Onglet « Rapports » de l'admin (demande du propriétaire) : ventes confirmées
// (commandes boutique payées + soumissions payées) PAR MOIS avec TPS/TVQ perçues,
// cumuls TRIMESTRIELS pour les remises de taxes et total annuel. Chaque MOIS et
// chaque TRIMESTRE est CLIQUABLE : le détail des ventes de la période s'affiche.
const MOIS_TRIMESTRE = [[1, 2, 3], [4, 5, 6], [7, 8, 9], [10, 11, 12]];

// Sélecteur de période « Du … Au … » (demande du propriétaire, 2026-10-06) : deux
// champs CALENDRIER (popup natif du navigateur), ajoutés EN PLUS des regroupements
// existants — les tableaux de rapports n'affichent que les lignes entre les 2 dates
const inRange = (d, from, to) => !!d && (!from || d >= from) && (!to || d <= to);

// Compilation des VENTES PERDUES (demande du propriétaire, 2026-10-06) : retraits
// par le client (raison libre classée PAR THÈME) + refus (motif choisi) — nombre,
// valeur perdue estimée et détail, pour comprendre où partent les ventes.
function WithdrawalsCard() {
  const money2 = (v) => `${(v || 0).toFixed(2)} $`;
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  // Relance « vente perdue » en un clic (demande du propriétaire, 2026-10-06) :
  // courriel personnalisé au client parti, directement depuis la liste
  const [relFor, setRelFor] = useState(null);   // ligne {id, name} à relancer
  const [relNote, setRelNote] = useState("");
  const [relBusy, setRelBusy] = useState(false);
  const [iaBusy, setIaBusy] = useState(false);          // rédaction de la note par Claude
  const relancer = async () => {
    setRelBusy(true);
    try {
      const { data: r } = await api.post(`/admin/quote-requests/${relFor.id}/relance-perdue`, { note: relNote.trim() });
      toast.success(`Courriel de relance envoyé à ${relFor.name}`);
      setData((d) => ({
        ...d,
        retraits: { ...d.retraits, items: d.retraits.items.map((x) => (x.id === relFor.id ? { ...x, relance_at: r.relance_perdue_at } : x)) },
        refus: { ...d.refus, items: d.refus.items.map((x) => (x.id === relFor.id ? { ...x, relance_at: r.relance_perdue_at } : x)) },
      }));
      setRelFor(null);
      setRelNote("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setRelBusy(false);
  };
  // Période « Du … Au … » (demande du propriétaire, 2026-10-06) : filtre la liste
  const [wFrom, setWFrom] = useState("");
  const [wTo, setWTo] = useState("");
  const charger = async (from = wFrom, to = wTo) => {
    try {
      const { data: d } = await api.get(`/admin/reports/withdrawals?date_from=${from}&date_to=${to}`);
      setData(d);
    } catch { setErr(true); }
  };
  const toggle = async () => {
    setOpen(!open);
    if (!open && !data) await charger();
  };
  // Recharger quand la période change (panneau ouvert)
  useEffect(() => { if (open && data) charger(wFrom, wTo); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [wFrom, wTo]);
  const Bars = ({ rows, barCls, testid }) => {
    const max = Math.max(1, ...rows.map((r) => r.nb));
    return (
      <div className="space-y-2" data-testid={testid}>
        {rows.map((r) => (
          <div key={r.key} className="flex items-center gap-3" data-testid={`${testid}-${r.key}`}>
            <span className="w-44 sm:w-56 shrink-0 text-xs text-ivory/70 truncate">{r.label}</span>
            <div className="flex-1 h-5 rounded bg-ink overflow-hidden">
              <div className={`h-full rounded ${barCls}`} style={{ width: `${(r.nb / max) * 100}%` }} />
            </div>
            <span className="w-28 shrink-0 text-right text-xs text-ivory/70">
              {r.nb} — <span className="text-champagne">{money2(r.valeur)}</span>
            </span>
          </div>
        ))}
      </div>
    );
  };
  return (
    <div className="mt-10 border-t border-gold/15 pt-8" data-testid="withdrawals-card">
      <button type="button" onClick={toggle} data-testid="withdrawals-toggle"
        className="w-full flex items-center gap-3 text-left group">
        <TrendingDown className="w-5 h-5 text-red-300 shrink-0" />
        <span className="min-w-0 flex-1">
          <span className="font-serif text-2xl text-ivory block group-hover:text-gold transition-colors">Ventes perdues — raisons</span>
          <span className="text-xs text-ivory/55 block mt-0.5">
            Retraits par le client (par thème) et refus (par motif) — valeur perdue estimée. Depuis le 1er août 2026, tests exclus.
          </span>
        </span>
        {open ? <ChevronDown className="w-4 h-4 text-gold shrink-0" /> : <ChevronRight className="w-4 h-4 text-gold shrink-0" />}
      </button>
      {open && (
        <div className="mt-5">
          <div className="mb-5">
            <RangePicker from={wFrom} to={wTo} setFrom={setWFrom} setTo={setWTo} idp="withdrawals" />
          </div>
          {err && <p className="text-sm text-red-300" data-testid="withdrawals-error">Rapport indisponible — réessayez.</p>}
          {!data && !err && (
            <p className="text-sm text-ivory/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Compilation…</p>
          )}
          {data && (
            <>
              <div className="flex flex-wrap gap-x-8 gap-y-2 mb-6" data-testid="withdrawals-total">
                <p className="text-sm text-ivory/70">
                  <strong className="text-red-300 text-lg">{data.retraits.total}</strong> retrait{data.retraits.total > 1 ? "s" : ""} par le client
                  {" — "}<strong className="text-red-300">{money2(data.retraits.valeur)}</strong> de valeur estimée
                </p>
                <p className="text-sm text-ivory/70">
                  <strong className="text-amber-300 text-lg">{data.refus.total}</strong> refus
                  {" — "}<strong className="text-amber-300">{money2(data.refus.valeur)}</strong>
                </p>
              </div>
              {/* Taux de sauvetage des contre-offres (demande du propriétaire,
                  2026-10-06) : mesure l'efficacité du rabais automatique */}
              {data.contre_offres && (
                <div className="mb-6 bg-ink border border-gold/20 rounded-xl p-4" data-testid="withdrawals-salvage">
                  <p className="text-[10px] uppercase tracking-widest text-gold mb-3">Contre-offres — taux de sauvetage</p>
                  {data.contre_offres.envoyees === 0 ? (
                    <p className="text-sm text-ivory/45" data-testid="salvage-empty">Aucune contre-offre envoyée pour l'instant.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      {[
                        ["Envoyées", data.contre_offres.envoyees, "text-ivory", "env"],
                        ["Acceptées", data.contre_offres.acceptees, "text-emerald-300", "ok"],
                        ["Refusées", data.contre_offres.refusees, "text-red-300", "no"],
                        ["En attente", data.contre_offres.en_attente, "text-amber-300", "wait"],
                      ].map(([lab, val, cls, tid]) => (
                        <div key={tid} className="bg-surface border border-ivory/10 rounded-lg px-3 py-2.5">
                          <p className="text-[9px] uppercase tracking-widest text-ivory/50">{lab}</p>
                          <p className={`font-serif text-xl mt-0.5 ${cls}`} data-testid={`salvage-${tid}`}>{val}</p>
                        </div>
                      ))}
                      <div className="bg-surface border border-emerald-400/30 rounded-lg px-3 py-2.5">
                        <p className="text-[9px] uppercase tracking-widest text-emerald-300/80">Ventes sauvées</p>
                        <p className="font-serif text-xl mt-0.5 text-emerald-300" data-testid="salvage-taux">{data.contre_offres.taux} %</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="grid lg:grid-cols-2 gap-8">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-red-300/80 mb-3">Retraits par le client — par thème</p>
                  {data.retraits.themes.length === 0 ? (
                    <p className="text-sm text-ivory/45" data-testid="withdrawals-retraits-empty">Aucun retrait pour l'instant.</p>
                  ) : (
                    <>
                      <Bars rows={data.retraits.themes} barCls="bg-red-400/70" testid="withdrawals-theme" />
                      <ul className="mt-4 space-y-1.5" data-testid="withdrawals-retraits-list">
                        {data.retraits.items.slice(0, 20).map((v) => (
                          <li key={v.id} className="text-xs text-ivory/60 flex flex-wrap gap-x-2" data-testid={`withdrawal-row-${v.id}`}>
                            <span className="text-ivory/40">{v.date}</span>
                            <span className="text-ivory/80 font-medium">{v.name}</span>
                            <span className="text-red-300/90">« {v.raison.length > 90 ? v.raison.slice(0, 90) + "…" : v.raison} »</span>
                            <button type="button" onClick={() => { setRelFor(v); setRelNote(""); }}
                              data-testid={`relance-${v.id}`} title="Renvoyer une offre personnalisée à ce client"
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-gold/80 hover:text-gold border border-gold/30 hover:border-gold/60 rounded-full px-2 py-0.5 transition-colors">
                              <Send className="w-2.5 h-2.5" /> {v.relance_at ? `Relancé ${v.relance_at.slice(0, 10)}` : "Relancer"}
                            </button>
                            <span className="text-champagne ml-auto">{money2(v.valeur)}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-amber-300/80 mb-3">Refus — par motif</p>
                  {data.refus.motifs.length === 0 ? (
                    <p className="text-sm text-ivory/45" data-testid="withdrawals-refus-empty">Aucun refus pour l'instant.</p>
                  ) : (
                    <>
                      <Bars rows={data.refus.motifs} barCls="bg-amber-400/70" testid="withdrawals-motif" />
                      <ul className="mt-4 space-y-1.5" data-testid="withdrawals-refus-list">
                        {data.refus.items.slice(0, 20).map((v) => (
                          <li key={v.id} className="text-xs text-ivory/60 flex flex-wrap gap-x-2" data-testid={`refusal-row-${v.id}`}>
                            <span className="text-ivory/40">{v.date}</span>
                            <span className="text-ivory/80 font-medium">{v.name}</span>
                            <span className="text-amber-300/90">{v.motif_label}{v.note ? ` — « ${v.note.length > 70 ? v.note.slice(0, 70) + "…" : v.note} »` : ""}</span>
                            <button type="button" onClick={() => { setRelFor(v); setRelNote(""); }}
                              data-testid={`relance-${v.id}`} title="Renvoyer une offre personnalisée à ce client"
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-gold/80 hover:text-gold border border-gold/30 hover:border-gold/60 rounded-full px-2 py-0.5 transition-colors">
                              <Send className="w-2.5 h-2.5" /> {v.relance_at ? `Relancé ${v.relance_at.slice(0, 10)}` : "Relancer"}
                            </button>
                            <span className="text-champagne ml-auto">{money2(v.valeur)}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {/* Popup de relance « vente perdue » (demande du propriétaire, 2026-10-06) :
          note personnalisée facultative — le courriel part au client avec le
          lien pour refaire une soumission en 30 secondes */}
      {relFor && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="relance-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setRelFor(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-md p-6 shadow-2xl shadow-black/60">
            <h3 className="font-serif text-2xl mb-1">Relancer {relFor.name}</h3>
            <p className="text-xs text-ivory/55 mb-4">
              Courriel « on aimerait mériter votre projet » avec lien pour refaire une soumission en 30 secondes.
            </p>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label htmlFor="relance-note" className="block text-xs uppercase tracking-widest text-ivory/60">
                Note personnalisée (facultative)
              </label>
              {/* Rédaction par Claude (demande du propriétaire) : brouillon modifiable */}
              <button type="button" disabled={iaBusy} data-testid="relance-ai"
                onClick={async () => {
                  setIaBusy(true);
                  try {
                    const { data } = await api.post("/admin/claude/rediger-courriel", {
                      contexte: "relance",
                      client: relFor.name,
                      details: `Raison du départ : ${relFor.raison || relFor.motif_label || "non précisée"} · valeur estimée : ${relFor.valeur} $`,
                      langue: "fr",
                    });
                    setRelNote(data.texte);
                    toast.success("Note rédigée par Claude — révisez-la avant l'envoi");
                  } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  setIaBusy(false);
                }}
                className="inline-flex items-center gap-1.5 text-[11px] text-gold hover:text-champagne border border-gold/30 hover:border-gold/60 rounded-full px-3 py-1 transition-colors disabled:opacity-50 shrink-0">
                {iaBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Rédiger avec Claude
              </button>
            </div>
            <textarea id="relance-note" rows={3} maxLength={1000}
              data-testid="relance-note" value={relNote} onChange={(e) => setRelNote(e.target.value)}
              placeholder="ex. : je peux vous accorder un rabais si vous confirmez cette semaine"
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors mb-4" />
            <button onClick={relancer} disabled={relBusy} data-testid="relance-confirm"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-6 py-3 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
              {relBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Envoyer la relance
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Compilation des ventes TEST / formation (demande du propriétaire, 2026-10-06) —
// TOUT EN BAS de la page Rapports, réservée aux ADMINISTRATEURS et déverrouillée
// par le CODE D'ACCÈS établi dans Paramètres. Ces ventes sont exclues des rapports
// de taxes ; ici on les compile au contraire (par mois + graphique + détail).
function TestSalesCard({ isAdmin }) {
  const cell = "px-3 py-2.5 text-sm";
  const cellR = `${cell} text-right`;
  const money2 = (v) => `${(v || 0).toFixed(2)} $`;
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!isAdmin) return null;

  const unlock = async () => {
    if (!code.trim()) { toast.error("Entrez le code d'accès établi dans Paramètres."); return; }
    setBusy(true);
    try {
      const { data: d } = await api.get(`/admin/reports/test-sales?code=${encodeURIComponent(code.trim())}`);
      setData(d);
      toast.success("Rapport Formation déverrouillé.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 border border-orange-500/25 rounded-xl" data-testid="test-sales-card">
      <button type="button" onClick={() => setOpen(!open)} data-testid="test-sales-toggle"
        className="w-full flex items-center gap-3 px-6 py-5 text-left hover:bg-orange-500/5 transition-colors duration-300 rounded-xl">
        <FlaskConical className="w-5 h-5 text-orange-300 shrink-0" />
        <span className="min-w-0 flex-1">
          <span className="font-serif text-xl text-ivory block">Formation</span>
          <span className="text-xs text-ivory/55 block mt-0.5">
            Compilation à part, confidentielle. Réservé aux administrateurs, code d'accès requis.
          </span>
        </span>
        {open ? <ChevronDown className="w-4 h-4 text-orange-300 shrink-0" /> : <ChevronRight className="w-4 h-4 text-orange-300 shrink-0" />}
      </button>
      {open && (
        <div className="px-6 pb-6 border-t border-orange-500/20 pt-5">
          {!data ? (
            <div className="flex flex-wrap items-center gap-3" data-testid="test-sales-lock">
              <Lock className="w-4 h-4 text-orange-300" />
              <input type="password" value={code} onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && unlock()}
                data-testid="test-sales-code" placeholder="Code d'accès (établi dans Paramètres)"
                autoComplete="off"
                className="bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm w-72 max-w-full focus:border-orange-300 focus:outline-none transition-colors duration-300" />
              <button type="button" onClick={unlock} disabled={busy} data-testid="test-sales-unlock"
                className="inline-flex items-center gap-2 bg-orange-500/90 text-ink font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-orange-400 transition-colors duration-300 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LockOpen className="w-4 h-4" />}
                Déverrouiller
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-5" data-testid="test-sales-total">
                <p className="text-sm text-ivory/70">
                  <strong className="text-orange-300 text-lg">{data.total.nb}</strong> dossier{data.total.nb > 1 ? "s" : ""} de formation
                  {" — "}<strong className="text-orange-300 text-lg">{money2(data.total.montant)}</strong> au total
                </p>
                <p className="text-xs text-ivory/50">
                  dont {data.total.nb_payees} payée{data.total.nb_payees > 1 ? "s" : ""} ({money2(data.total.montant_paye)})
                </p>
                <button type="button" onClick={() => { setData(null); setCode(""); }} data-testid="test-sales-relock"
                  className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-ivory/45 hover:text-orange-300 transition-colors">
                  <Lock className="w-3 h-3" /> Reverrouiller
                </button>
              </div>
              {data.mois.length > 0 && (
                <div className="mb-6" data-testid="test-sales-chart">
                  <p className="text-[10px] uppercase tracking-widest text-ivory/50 mb-2">Ventes Formation par mois (total après taxes)</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data.mois} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke="rgba(245,237,216,0.08)" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: "rgba(245,237,216,0.6)", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: "rgba(245,237,216,0.6)", fontSize: 11 }} axisLine={false} tickLine={false}
                        tickFormatter={(v) => `${v} $`} width={70} />
                      <Tooltip cursor={{ fill: "rgba(212,175,55,0.08)" }}
                        contentStyle={{ background: "#141414", border: "1px solid rgba(251,146,60,0.4)", borderRadius: 10, fontSize: 12 }}
                        formatter={(v) => [`${Number(v).toFixed(2)} $`, "Total"]} />
                      <Bar dataKey="total" fill="#fb923c" radius={[6, 6, 0, 0]} maxBarSize={56} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              {data.ventes.length === 0 ? (
                <p className="text-sm text-ivory/50" data-testid="test-sales-empty">Aucun dossier de formation pour l'instant.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[520px]" data-testid="test-sales-table">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-widest text-orange-300/80 border-b border-orange-500/20">
                        <th className={cell}>Date</th>
                        <th className={cell}>Dossier</th>
                        <th className={cell}>Type</th>
                        <th className={cell}>Payée</th>
                        <th className={cellR}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.ventes.map((v) => (
                        <tr key={`${v.kind}-${v.id}`} data-testid={`test-sales-row-${v.id}`} className="border-b border-ivory/8">
                          <td className={`${cell} text-ivory/60`}>{v.date}</td>
                          <td className={cell}>{v.name}</td>
                          <td className={`${cell} text-ivory/60 text-xs`}>{v.kind === "commande" ? "Boutique" : "Soumission"}</td>
                          <td className={cell}>
                            <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border ${v.paye ? "border-emerald-500/40 text-emerald-300" : "border-ivory/20 text-ivory/45"}`}>
                              {v.paye ? "Payée" : "Non payée"}
                            </span>
                          </td>
                          <td className={`${cellR} text-champagne`}>{money2(v.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function TaxReportTab({ isAdmin = false }) {
  const anneeCourante = new Date().getFullYear();
  const [year, setYear] = useState(anneeCourante);
  // Sous-onglets Rapports (demande du propriétaire, 2026-10-06) : « Ventes et
  // taxes » (existant) et « Comptes payables » (factures fournisseurs, admins seuls)
  const [sous, setSous] = useState("taxes");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);  // {type:"mois",m} | {type:"trimestre",q} | null
  const [moisVentes, setMoisVentes] = useState(null); // popup « ventes du mois » (clic sur la courbe, 2026-10-23)
  // Classement des ventes passées (demande du propriétaire) : panneau listant les
  // ventes payées sans catégorie, avec suggestion automatique par ligne
  const [showClasser, setShowClasser] = useState(false);
  const [unclassed, setUnclassed] = useState(null);        // null = pas encore chargé
  const [classerDraft, setClasserDraft] = useState({});    // {id vente: {index ligne: catégorie}}
  const [classerBusy, setClasserBusy] = useState(false);
  // Période personnalisée « Du … Au … » (demande du propriétaire, 2026-10-06) :
  // filtre calendrier EN PLUS des regroupements — ventes cumulées et détail période
  const [cFrom, setCFrom] = useState("");
  const [cTo, setCTo] = useState("");
  const [dFrom, setDFrom] = useState("");
  const [dTo, setDTo] = useState("");
  // Période « Du … Au … » du tableau VENTES PAR CATÉGORIE (demande du propriétaire,
  // 2026-10-06) — calculée côté serveur (répartition au prorata des lignes)
  const [catFrom, setCatFrom] = useState("");
  const [catTo, setCatTo] = useState("");
  const pendingSel = useRef(null);  // mois choisi sur la courbe en attendant le changement d'année
  const [courbeCat, setCourbeCat] = useState("");  // filtre par catégorie de la courbe (2026-10-22)
  const [courbeVendeur, setCourbeVendeur] = useState("");  // filtre par vendeur de la courbe (2026-10-22)
  const [vendeursNoms, setVendeursNoms] = useState({});    // {courriel: nom} — réservé admin (403 sinon)
  // Objectif de ventes du mois (demande du propriétaire, 2026-10-22) : réglé dans
  // Paramètres — ligne pointillée sur la courbe des 12 mois
  const [objectif, setObjectif] = useState(0);
  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => setObjectif(Number(r.data?.objectif_mensuel) || 0))
      .catch(() => {});
    // Noms des vendeurs pour le filtre « par vendeur » de la courbe (liste
    // réservée à l'admin — un vendeur ne voit tout simplement pas le filtre)
    api.get("/admin/vendeurs").then((r) => {
      const m = {};
      (r.data?.vendeurs || []).forEach((v) => { if (v.email) m[v.email] = v.name || v.email; });
      setVendeursNoms(m);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let stop = false;
    setLoading(true);
    // Courbe CLIQUABLE (demande du propriétaire, 2026-10-22) : un clic sur un
    // mois d'une AUTRE année change d'année — la sélection est alors reprise ici
    setSel(pendingSel.current);
    pendingSel.current = null;
    api.get(`/admin/reports/taxes?year=${year}&cat_from=${catFrom}&cat_to=${catTo}`)
      .then((r) => { if (!stop) setData(r.data); })
      .catch(() => { if (!stop) setData(null); })
      .finally(() => { if (!stop) setLoading(false); });
    return () => { stop = true; };
  }, [year, catFrom, catTo]);

  // Courbe des 12 DERNIERS mois (demande du propriétaire, 2026-10-22) : pour
  // l'année courante, les premiers mois de la courbe vivent dans l'année
  // précédente — on la charge en appoint (rien à charger pour une année passée)
  const [dataPrev, setDataPrev] = useState(null);
  useEffect(() => {
    let stop = false;
    if (year === anneeCourante) {
      api.get(`/admin/reports/taxes?year=${year - 1}&cat_from=${catFrom}&cat_to=${catTo}`)
        .then((r) => { if (!stop) setDataPrev(r.data); })
        .catch(() => {});
    } else setDataPrev(null);
    return () => { stop = true; };
  }, [year, catFrom, catTo]);

  const money = (v) => `${(v || 0).toFixed(2)} $`;
  const MOIS_COURT = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juill.", "août", "sept.", "oct.", "nov.", "déc."];
  // Maintenant à MONTRÉAL (règle permanente : toutes les dates au fuseau de l'atelier)
  const maintenantMtl = () => new Date(new Date().toLocaleString("en-US", { timeZone: "America/Montreal" }));
  const annees = [anneeCourante, anneeCourante - 1, anneeCourante - 2];

  // ---- Classement des ventes passées ----
  const loadUnclassed = () => {
    setShowClasser(true);
    setUnclassed(null);
    api.get("/admin/reports/uncategorized-sales").then((r) => {
      const ventes = r.data.ventes || [];
      setUnclassed(ventes);
      // Pré-remplir chaque ligne avec la SUGGESTION automatique (l'admin ajuste)
      const d = {};
      for (const v of ventes) {
        d[v.id] = {};
        for (const l of v.lignes) d[v.id][l.index] = l.suggestion || "";
      }
      setClasserDraft(d);
    }).catch(() => { setUnclassed([]); toast.error("Chargement impossible — réessayez."); });
  };
  const applyCategories = async (updates) => {
    setClasserBusy(true);
    try {
      const { data: res } = await api.post("/admin/reports/uncategorized-sales/apply", { updates });
      toast.success(`${res.done} dossier(s) classé(s) — les statistiques sont à jour.`);
      const ids = new Set(updates.map((u) => u.id));
      setUnclassed((xs) => (xs || []).filter((v) => !ids.has(v.id)));
      api.get(`/admin/reports/taxes?year=${year}&cat_from=${catFrom}&cat_to=${catTo}`).then((r) => setData(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du classement.");
    } finally { setClasserBusy(false); }
  };
  const applyOne = (v) => {
    const clean = Object.fromEntries(
      Object.entries(classerDraft[v.id] || {}).filter(([, c]) => c));
    if (!Object.keys(clean).length) { toast.error("Choisissez au moins une catégorie."); return; }
    applyCategories([{ id: v.id, categories: clean }]);
  };
  const applyAllSuggestions = () => {
    const updates = (unclassed || []).map((v) => ({
      id: v.id,
      categories: Object.fromEntries(
        v.lignes.filter((l) => l.suggestion).map((l) => [String(l.index), l.suggestion])),
    })).filter((u) => Object.keys(u.categories).length);
    if (!updates.length) { toast.error("Aucune suggestion automatique à appliquer."); return; }
    applyCategories(updates);
  };
  const cell = "px-3 py-2.5 text-sm";
  const cellR = `${cell} text-right`;

  const detail = useMemo(() => {
    if (!sel || !data) return null;
    const moisInclus = sel.type === "mois" ? [sel.m] : MOIS_TRIMESTRE[sel.q - 1];
    const lignes = (data.ventes || []).filter((v) => moisInclus.includes(v.mois)
      // Période personnalisée « Du … Au … » (demande du propriétaire, 2026-10-06)
      && inRange(v.date, dFrom, dTo));
    const totaux = lignes.reduce((t, v) => ({
      avant_taxes: t.avant_taxes + v.avant_taxes, tps: t.tps + v.tps,
      tvq: t.tvq + v.tvq, tvh: t.tvh + (v.tvh || 0), total: t.total + v.total,
    }), { avant_taxes: 0, tps: 0, tvq: 0, tvh: 0, total: 0 });
    // Chiffre d'affaires PAR JOUR quand un mois est choisi (demande du propriétaire)
    const jours = sel.type === "mois" ? Object.values(lignes.reduce((acc, v) => {
      const j = acc[v.date] || (acc[v.date] = { jour: v.date, nb: 0, avant_taxes: 0, tps: 0, tvq: 0, total: 0 });
      j.nb += 1;
      j.avant_taxes += v.avant_taxes; j.tps += v.tps; j.tvq += v.tvq; j.total += v.total;
      return acc;
    }, {})) : null;
    const titre = sel.type === "mois"
      ? `${data.mois[sel.m - 1].label} ${year}`
      : `${data.trimestres[sel.q - 1].label} ${year}`;
    const printUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/taxes/print?year=${year}`
      + (sel.type === "mois" ? `&mois=${sel.m}` : `&trimestre=${sel.q}`);
    return { lignes, totaux, titre, jours, printUrl };
  }, [sel, data, year, dFrom, dTo]);

  // Ventes CUMULÉES depuis le 1er septembre (demande du propriétaire) — regroupées
  // par jour, semaine (lun-dim), mois ou trimestre, avec colonne « Cumul »
  const [cumulPar, setCumulPar] = useState("jour");
  const cumulRows = useMemo(() => {
    if (!data) return [];
    const debut = `${year}-09-01`;
    const lundi = (iso) => {
      const d = new Date(iso + "T12:00:00");
      d.setDate(d.getDate() - ((d.getDay() + 6) % 7));  // recule au lundi
      return dayKeyMtl(d);  // clé de jour à Montréal (règle permanente)
    };
    const clefs = {
      jour: (v) => v.date,
      semaine: (v) => `Sem. du ${lundi(v.date).slice(8, 10)} ${(data.mois[Number(lundi(v.date).slice(5, 7)) - 1] || {}).label?.slice(0, 4).toLowerCase() || ""}.`,
      mois: (v) => (data.mois[v.mois - 1] || {}).label || v.date,
      trimestre: (v) => (data.trimestres[Math.ceil(v.mois / 3) - 1] || {}).label || "",
    };
    const tri = { jour: (v) => v.date, semaine: (v) => lundi(v.date), mois: (v) => v.mois, trimestre: (v) => Math.ceil(v.mois / 3) };
    const groupes = new Map();
    for (const v of (data.ventes || [])) {
      // Période personnalisée « Du … Au … » (demande du propriétaire, 2026-10-06) :
      // le « Du » choisi prime sur le point de départ par défaut (1er septembre)
      if (!v.date || v.date < (cFrom || debut) || (cTo && v.date > cTo)) continue;
      const c = clefs[cumulPar](v);
      const g = groupes.get(c) || { periode: c, ordre: tri[cumulPar](v), nb: 0, avant_taxes: 0, tps: 0, tvq: 0, total: 0 };
      g.nb += 1;
      g.avant_taxes += v.avant_taxes; g.tps += v.tps; g.tvq += v.tvq; g.total += v.total;
      groupes.set(c, g);
    }
    // « Par mois » (demande du propriétaire, 2026-10-22) : le MOIS COURANT paraît
    // toujours, même sans vente encore — pour suivre le mois en cours d'un coup
    // d'œil (vue par défaut seulement ; une période Du/Au choisie reste telle quelle)
    if (cumulPar === "mois" && year === anneeCourante && !cFrom && !cTo) {
      const mCourant = maintenantMtl().getMonth() + 1;
      const cleCourante = (data.mois[mCourant - 1] || {}).label;
      if (cleCourante && !groupes.has(cleCourante)) {
        groupes.set(cleCourante, { periode: cleCourante, ordre: mCourant, nb: 0, avant_taxes: 0, tps: 0, tvq: 0, total: 0 });
      }
    }
    let cumul = 0;
    return [...groupes.values()]
      .sort((a, b) => (a.ordre < b.ordre ? -1 : 1))
      .map((g) => ({ ...g, cumul: (cumul = Math.round((cumul + g.total) * 100) / 100) }));
  }, [data, year, cumulPar, cFrom, cTo]);

  // COURBE des ventes des 12 derniers mois (demande du propriétaire, 2026-10-22)
  // — dans la section des ventes cumulées ; le mois courant y est toujours,
  // marqué « en cours ». Année passée choisie → les 12 mois de cette année-là.
  const courbe12 = useMemo(() => {
    if (!data) return [];
    const ventes = [...((year === anneeCourante && dataPrev?.ventes) || []), ...(data.ventes || [])];
    const mtl = maintenantMtl();
    const finA = year === anneeCourante ? mtl.getFullYear() : year;
    const finM = year === anneeCourante ? mtl.getMonth() + 1 : 12;
    const cleCourante = `${mtl.getFullYear()}-${String(mtl.getMonth() + 1).padStart(2, "0")}`;
    const parMois = new Map();
    for (const v of ventes) {
      if (!v.date) continue;
      // Filtre par VENDEUR (demande du propriétaire, 2026-10-22) : seules les
      // ventes attribuées au vendeur choisi (créateur de la fiche ou case
      // « visible par ») — les commandes boutique n'ont pas d'attribution
      if (courbeVendeur && !(v.vendeurs || []).includes(courbeVendeur)) continue;
      // Filtre par catégorie (demande du propriétaire, 2026-10-22) : part TTC de la
      // catégorie choisie (prorata du serveur) ; les commandes boutique non
      // classées n'entrent dans aucun filtre
      const montant = courbeCat ? ((v.cat_totaux || {})[courbeCat] || 0) : v.total;
      if (!montant) continue;
      const cle = v.date.slice(0, 7);
      parMois.set(cle, Math.round(((parMois.get(cle) || 0) + montant) * 100) / 100);
    }
    const points = [];
    for (let i = 11; i >= 0; i--) {
      let a = finA, m = finM - i;
      while (m < 1) { m += 12; a -= 1; }
      const cle = `${a}-${String(m).padStart(2, "0")}`;
      points.push({ cle, label: MOIS_COURT[m - 1], total: parMois.get(cle) || 0, enCours: cle === cleCourante });
    }
    return points;
  }, [data, dataPrev, year, courbeCat, courbeVendeur]);

  // Catégories présentes dans les ventes des 12 mois (pour le filtre de la courbe)
  const courbeCategories = useMemo(() => {
    const noms = new Set();
    for (const v of [...(dataPrev?.ventes || []), ...((data && data.ventes) || [])]) {
      Object.keys(v.cat_totaux || {}).forEach((k) => noms.add(k));
    }
    return [...noms].sort((a, b) => a.localeCompare(b, "fr"));
  }, [data, dataPrev]);

  // Géométrie de la courbe (SVG 720 × 150, responsive)
  const courbeMax = Math.max(...courbe12.map((p) => p.total), objectif || 0, 1);
  const courbeX = (i) => 24 + (i * 672) / 11;
  const courbeY = (v) => 118 - (v / courbeMax) * 96;
  const courbePts = courbe12.map((p, i) => `${courbeX(i)},${courbeY(p.total)}`).join(" ");
  // Courbe CLIQUABLE (demande du propriétaire, 2026-10-22) : un clic sur un mois
  // ouvre le détail de ses ventes (le mois d'une autre année change d'année au passage)
  const ouvrirMoisCourbe = (p) => {
    const a = Number(p.cle.slice(0, 4)), m = Number(p.cle.slice(5, 7));
    if (a !== year) {
      pendingSel.current = { type: "mois", m };
      setYear(a);
    } else {
      setSel({ type: "mois", m });
    }
    setTimeout(() => document.querySelector('[data-testid="tax-detail"]')
      ?.scrollIntoView({ behavior: "smooth", block: "start" }), 500);
    // COURBE CLIQUABLE (demande du propriétaire, 2026-10-23) : le clic ouvre
    // AUSSI le détail des VENTES du mois (chaque facture), pas seulement la
    // ligne de taxes — popup direct
    setMoisVentes({ cle: p.cle, label: `${p.label} ${a}`, data: null });
    api.get(`/admin/reports/ventes-mois?a=${a}&m=${m}`)
      .then((r) => setMoisVentes((v) => (v && v.cle === p.cle ? { ...v, data: r.data } : v)))
      .catch(() => setMoisVentes((v) => (v && v.cle === p.cle ? { ...v, data: { ventes: [], nb: 0, total: 0 } } : v)));
  };

  const toggleMois = (m) => setSel((s) => (s?.type === "mois" && s.m === m ? null : { type: "mois", m }));
  const toggleTrimestre = (q) => setSel((s) => (s?.type === "trimestre" && s.q === q ? null : { type: "trimestre", q }));

  return (
    <div data-testid="tax-report-tab" className="bg-surface border border-gold/15 rounded-xl p-6 sm:p-8">
      {isAdmin && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="rapports-subtabs">
          <button onClick={() => setSous("taxes")} data-testid="rapports-sub-taxes"
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${sous === "taxes" ? "bg-gold text-ink" : "border border-ivory/25 text-ivory/70 hover:border-gold hover:text-gold"}`}>
            Ventes et taxes
          </button>
          <button onClick={() => setSous("payables")} data-testid="rapports-sub-payables"
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${sous === "payables" ? "bg-gold text-ink" : "border border-ivory/25 text-ivory/70 hover:border-gold hover:text-gold"}`}>
            Comptes payables
          </button>
        </div>
      )}
      {isAdmin && sous === "payables" && <ComptesPayables />}
      <div className={isAdmin && sous === "payables" ? "hidden" : undefined}>
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <h3 className="font-serif text-2xl flex items-center gap-2">
          <FileBarChart className="w-5 h-5 text-gold" /> Rapport des ventes et taxes
        </h3>
        <label className="ml-auto flex items-center gap-2 text-xs text-ivory/55">
          Année :
          <select data-testid="tax-report-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="bg-ink border border-ivory/15 rounded-lg px-2.5 py-1.5 text-xs text-ivory focus:border-gold focus:outline-none transition-colors duration-300">
            {annees.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        {/* Version imprimable pour le comptable (demande du propriétaire) — page blanche sobre */}
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/taxes/print?year=${year}`}
          target="_blank" rel="noreferrer" data-testid="tax-print-year"
          title="Rapport annuel imprimable (par mois + par trimestre)"
          className="inline-flex items-center gap-1.5 border border-ivory/25 text-ivory/80 px-4 py-2 rounded-full text-xs hover:border-gold hover:text-gold transition-colors duration-300">
          <Printer className="w-3.5 h-3.5" /> Imprimer l'année
        </a>
        {/* Export EXCEL (demande du propriétaire — comptable) : tableau des ventes
            par jour en chiffres + graphique, détail des ventes, mois et trimestres */}
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/taxes/excel?year=${year}`}
          target="_blank" rel="noreferrer" data-testid="tax-excel"
          title="Export Excel : ventes par jour en chiffres + graphique, détail, mois et trimestres"
          className="inline-flex items-center gap-1.5 border border-emerald-500/40 text-emerald-300 px-4 py-2 rounded-full text-xs hover:bg-emerald-500 hover:text-ink transition-colors duration-300">
          <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
        </a>
      </div>
      <p className="text-xs text-ivory/50 mb-5">
        Ventes confirmées seulement (commandes boutique payées + factures payées), mois à l'heure de Montréal,
        <strong className="text-ivory/70"> comptabilisées depuis le 1er août 2026.</strong>
        <strong className="text-ivory/70"> Cliquez sur un mois ou un trimestre pour voir le détail de ses ventes.</strong>
      </p>

      {loading && (
        <p className="text-sm text-ivory/50 flex items-center gap-2" data-testid="tax-report-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Calcul du rapport…
        </p>
      )}

      {!loading && data && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]" data-testid="tax-report-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-gold border-b border-gold/20">
                  <th className={cell}></th>
                  <th className={cell}>Mois</th>
                  <th className={`${cellR} text-ivory/60`}>Ventes</th>
                  <th className={cellR}>Avant taxes</th>
                  <th className={cellR}>TPS</th>
                  <th className={cellR}>TVQ</th>
                  <th className={cellR}>Total</th>
                  <th className={cell} title="Imprimer le rapport du mois"></th>
                </tr>
              </thead>
              <tbody>
                {data.mois.map((m) => {
                  const ouvert = sel?.type === "mois" && sel.m === m.mois;
                  return (
                    <tr key={m.mois} data-testid={`tax-month-${m.mois}`} onClick={() => toggleMois(m.mois)}
                      title={`Voir le détail de ${m.label}`}
                      className={`border-b border-ivory/8 cursor-pointer transition-colors duration-200 hover:bg-gold/5 ${ouvert ? "bg-gold/10 border-l-2 border-l-gold" : ""} ${m.nb ? "" : "opacity-35"}`}>
                      <td className={`${cell} w-8 text-gold`}>
                        {ouvert ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </td>
                      <td className={cell}>{m.label}</td>
                      <td className={`${cellR} text-ivory/60`}>{m.nb}</td>
                      <td className={cellR}>{money(m.avant_taxes)}</td>
                      <td className={cellR}>{money(m.tps)}</td>
                      <td className={cellR}>{money(m.tvq)}</td>
                      <td className={`${cellR} font-semibold`}>{money(m.total)}</td>
                      {/* Imprimer CE mois (demande du propriétaire) — sans ouvrir le détail */}
                      <td className={`${cell} w-10`}>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/taxes/print?year=${year}&mois=${m.mois}`}
                          target="_blank" rel="noreferrer" data-testid={`tax-print-month-${m.mois}`}
                          onClick={(e) => e.stopPropagation()}
                          title={`Imprimer le rapport de ${m.label} ${year}`}
                          className="inline-flex p-1.5 rounded-lg border border-ivory/15 text-ivory/50 hover:border-gold hover:text-gold transition-colors duration-200">
                          <Printer className="w-3.5 h-3.5" />
                        </a>
                      </td>
                    </tr>
                  );
                })}
                <tr className="text-gold font-bold" data-testid="tax-year-total">
                  <td className={cell}></td>
                  <td className={cell}>Total {data.annee.label.replace("Année ", "")}</td>
                  <td className={cellR}>{data.annee.nb}</td>
                  <td className={cellR}>{money(data.annee.avant_taxes)}</td>
                  <td className={cellR}>{money(data.annee.tps)}</td>
                  <td className={cellR}>{money(data.annee.tvq)}</td>
                  <td className={cellR}>{money(data.annee.total)}</td>
                  <td className={cell}></td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* Ventes EXEMPTÉES de taxes (demande du propriétaire — pour le comptable) */}
          {data.annee.exempt_nb > 0 && (
            <p className="text-sm text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 rounded-xl px-5 py-3 mt-4"
              data-testid="tax-year-exempt">
              dont ventes <strong>exemptées de taxes</strong> : {data.annee.exempt_nb} vente{data.annee.exempt_nb > 1 ? "s" : ""}
              {" · "}{money(data.annee.exempt_avant)} avant taxes (0 $ de taxes perçues)
            </p>
          )}

          <p className="text-[11px] uppercase tracking-widest text-gold mt-8 mb-3">
            Rapports trimestriels (remises TPS/TVQ) — cliquez pour le détail
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.trimestres.map((t, i) => {
              const ouvert = sel?.type === "trimestre" && sel.q === i + 1;
              return (
                <div key={t.label} onClick={() => toggleTrimestre(i + 1)} data-testid={`tax-quarter-${i + 1}`}
                  role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") toggleTrimestre(i + 1); }}
                  className={`text-left bg-ink border rounded-xl p-4 cursor-pointer transition-all duration-200 hover:border-gold/50 ${
                    ouvert ? "border-gold shadow-[0_0_14px_rgba(212,175,55,0.25)]" : "border-ivory/10"}`}>
                  <p className="text-xs text-gold font-bold mb-2 flex items-center gap-1.5">
                    {ouvert ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    {t.label}
                    {/* Imprimer CE trimestre (demande du propriétaire) — sans ouvrir le détail */}
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/taxes/print?year=${year}&trimestre=${i + 1}`}
                      target="_blank" rel="noreferrer" data-testid={`tax-print-quarter-${i + 1}`}
                      onClick={(e) => e.stopPropagation()}
                      title={`Imprimer le rapport ${t.label} ${year}`}
                      className="ml-auto inline-flex p-1.5 rounded-lg border border-ivory/15 text-ivory/50 hover:border-gold hover:text-gold transition-colors duration-200">
                      <Printer className="w-3.5 h-3.5" />
                    </a>
                  </p>
                  <p className="text-xs text-ivory/60">{t.nb} vente{t.nb > 1 ? "s" : ""} · avant taxes {money(t.avant_taxes)}</p>
                  <p className="text-xs text-ivory/80 mt-1">TPS : <strong>{money(t.tps)}</strong> · TVQ : <strong>{money(t.tvq)}</strong></p>
                  {/* Total combiné des 2 taxes du trimestre (demande du propriétaire)
                      — le montant à remettre, sans calcul à la main */}
                  <p className="text-xs mt-1 text-emerald-300">TPS + TVQ à remettre : <strong data-testid={`tax-quarter-taxes2-${i + 1}`}>{money(t.tps + t.tvq)}</strong></p>
                  <p className="text-sm text-ivory mt-1">Total : <strong className="text-gold">{money(t.total)}</strong></p>
                </div>
              );
            })}
          </div>

          {/* Ventes par CATÉGORIE (demande du propriétaire) : provenance des ventes
              classées manuellement dans la fiche de chaque dossier (admin seulement) —
              statistiques à long terme, par année. Les commandes boutique en ligne
              ne sont pas classées ici. */}
          {data.par_categorie && (
            <div className="mt-10" data-testid="tax-categories">
              <p className="text-[11px] uppercase tracking-widest text-gold mb-1">
                Ventes par catégorie — {year}
              </p>
              <p className="text-xs text-ivory/45 mb-3">
                Chaque choix est classé dans sa fiche (« Catégorie de vente », sous les prix). La quantité vendue compte les articles (ex. : une commande de 300 trophées ajoute 300 à sa catégorie) ; les montants d'une vente à plusieurs catégories sont répartis au prorata des lignes. Les ventes de la boutique en ligne ne sont pas classées.
              </p>
              <div className="mb-4">
                <RangePicker from={catFrom} to={catTo} setFrom={setCatFrom} setTo={setCatTo} idp="tax-cat" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px]" data-testid="tax-categories-table">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-widest text-gold border-b border-gold/20">
                      <th className={cell}>Catégorie</th>
                      <th className={`${cellR} text-ivory/60`}>Quantité vendue</th>
                      <th className={cellR}>Avant taxes</th>
                      <th className={cellR}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.par_categorie.map((c) => (
                      <tr key={c.key} data-testid={`tax-category-${c.key}`}
                        className={`border-b border-ivory/8 ${c.qte === 0 ? "opacity-40" : ""}`}>
                        <td className={cell}>{c.label}</td>
                        <td className={`${cellR} text-ivory/60`} data-testid={`tax-category-qte-${c.key}`}>{c.qte}</td>
                        <td className={cellR}>{money(c.avant_taxes)}</td>
                        <td className={`${cellR} font-semibold text-gold`}>{money(c.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Classement des ventes passées (demande du propriétaire) : ventes payées
              sans catégorie, avec suggestion automatique par choix — l'admin ajuste
              puis applique, dossier par dossier ou toutes les suggestions d'un coup */}
          <div className="mt-6" data-testid="tax-classer">
            {!showClasser ? (
              <button onClick={loadUnclassed} data-testid="tax-classer-open"
                className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-2 rounded-full text-xs hover:bg-gold/10 transition-colors duration-300">
                <Wand2 className="w-3.5 h-3.5" /> Classer les ventes passées
              </button>
            ) : (
              <div className="rounded-xl border border-gold/20 bg-ink/40 p-4">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <p className="text-[11px] uppercase tracking-widest text-gold">
                    Ventes payées à classer
                  </p>
                  <div className="ml-auto flex gap-2">
                    <button onClick={applyAllSuggestions} disabled={classerBusy || !unclassed?.length}
                      data-testid="tax-classer-apply-all"
                      className="inline-flex items-center gap-1.5 bg-gold text-ink text-xs font-bold px-4 py-1.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-50">
                      {classerBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                      Appliquer toutes les suggestions
                    </button>
                    <button onClick={() => setShowClasser(false)} data-testid="tax-classer-close"
                      className="text-xs text-ivory/50 hover:text-gold transition-colors duration-300">
                      Fermer
                    </button>
                  </div>
                </div>
                {unclassed === null ? (
                  <p className="text-sm text-ivory/50 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                  </p>
                ) : unclassed.length === 0 ? (
                  <p className="text-sm text-emerald-300" data-testid="tax-classer-empty">
                    Toutes les ventes payées sont classées — les statistiques par catégorie sont complètes.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {unclassed.map((v) => (
                      <div key={v.id} className="border-b border-ivory/8 pb-3" data-testid={`classer-sale-${v.id}`}>
                        <p className="text-xs text-ivory/70 mb-1.5">
                          <strong className="text-ivory">{v.client}</strong>
                          {" · "}{v.date}{" · "}{money(v.avant_taxes)} avant taxes
                        </p>
                        <div className="space-y-1.5">
                          {v.lignes.map((l) => (
                            <div key={l.index} className="flex flex-wrap items-center gap-2">
                              <span className="text-xs text-ivory/60 min-w-0 flex-1">
                                Choix {l.index + 1} — {l.produit} <span className="text-ivory/40">(×{l.qte})</span>
                              </span>
                              {l.suggestion && (
                                <span className="text-[9px] uppercase tracking-wider text-champagne/70"
                                  title="Suggestion automatique d'après le nom du produit — vérifiez avant d'appliquer">
                                  suggéré
                                </span>
                              )}
                              <select value={classerDraft[v.id]?.[l.index] ?? ""}
                                onChange={(e) => setClasserDraft((d) => ({
                                  ...d, [v.id]: { ...(d[v.id] || {}), [l.index]: e.target.value } }))}
                                data-testid={`classer-select-${v.id}-${l.index}`}
                                className="bg-ink border border-ivory/15 rounded-lg px-2 py-1 text-xs focus:border-gold focus:outline-none [color-scheme:dark]">
                                <option value="">(Non classée)</option>
                                {CATEGORIES.map(([key, lab]) => (
                                  <option key={key} value={key}>{lab}</option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                        <button onClick={() => applyOne(v)} disabled={classerBusy}
                          data-testid={`classer-apply-${v.id}`}
                          className="mt-2 text-xs font-semibold text-gold hover:underline disabled:opacity-50">
                          Appliquer pour ce dossier
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Ventes CUMULÉES depuis le 1er septembre (demande du propriétaire) :
              par jour, semaine (lun-dim), mois ou 3 mois (colonne Cumul retirée 2026-10-22) */}
          <div className="mt-10" data-testid="tax-cumul">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <p className="text-[11px] uppercase tracking-widest text-gold">
                Ventes cumulées {cFrom || cTo
                  ? `— période du ${cFrom || "début"} au ${cTo || "aujourd'hui"}`
                  : <>depuis le 1<sup>er</sup> septembre</>}
              </p>
              {/* Période personnalisée (calendriers Du/Au) — EN PLUS des regroupements */}
              <RangePicker idp="cumul" from={cFrom} to={cTo} setFrom={setCFrom} setTo={setCTo} />
              <div className="ml-auto flex gap-1.5" data-testid="tax-cumul-groupes">
                {[["jour", "Par jour"], ["semaine", "Par semaine"], ["mois", "Par mois"], ["trimestre", "Par 3 mois"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setCumulPar(k)} data-testid={`tax-cumul-${k}`}
                    className={`px-3.5 py-1.5 rounded-full text-xs border transition-colors duration-200 ${
                      cumulPar === k ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
                    {lbl}
                  </button>
                ))}
                {/* Export EXCEL du tableau tel qu'affiché (demande du propriétaire,
                    2026-10-22 — pour le comptable) : regroupement et période Du/Au respectés */}
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/taxes/cumulees/excel?year=${year}&par=${cumulPar}${cFrom ? `&du=${cFrom}` : ""}${cTo ? `&au=${cTo}` : ""}`}
                  target="_blank" rel="noreferrer" data-testid="tax-cumul-excel"
                  title="Télécharger ce tableau en Excel (regroupement et période affichés) — prêt pour le comptable"
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500 hover:text-ink transition-colors duration-200">
                  <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
                </a>
              </div>
            </div>
            {/* Courbe des ventes des 12 derniers mois (demande du propriétaire,
                2026-10-22) — au-dessus du tableau des ventes cumulées ; le mois
                courant est toujours là, marqué « en cours ». Survol = montant exact */}
            {courbe12.length > 0 && (
              <div className="mb-6" data-testid="tax-courbe-12m">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <p className="text-[11px] uppercase tracking-widest text-gold">
                    Ventes des 12 derniers mois — total TTC par mois
                  </p>
                  {/* Filtre par catégorie de vente (demande du propriétaire,
                      2026-10-22) : la courbe ne garde que la part de la catégorie
                      choisie ; les commandes boutique non classées en sont exclues */}
                  {courbeCategories.length > 0 && (
                    <select value={courbeCat} onChange={(e) => setCourbeCat(e.target.value)}
                      data-testid="tax-courbe-cat"
                      title="Filtrer la courbe par catégorie de vente"
                      className="bg-ink border border-ivory/15 rounded-lg px-2.5 py-1 text-[11px] text-ivory focus:border-gold focus:outline-none transition-colors duration-300">
                      <option value="">Toutes les catégories</option>
                      {courbeCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                  {/* Filtre par VENDEUR (demande du propriétaire, 2026-10-22) :
                      ventes créées par le vendeur ou cochées « visible par » lui —
                      réservé à l'admin (la liste des vendeurs n'est pas servie aux autres) */}
                  {Object.keys(vendeursNoms).length > 0 && (
                    <select value={courbeVendeur} onChange={(e) => setCourbeVendeur(e.target.value)}
                      data-testid="tax-courbe-vendeur"
                      title="Filtrer la courbe par vendeur (fiches créées ou assignées)"
                      className="bg-ink border border-ivory/15 rounded-lg px-2.5 py-1 text-[11px] text-ivory focus:border-gold focus:outline-none transition-colors duration-300">
                      <option value="">Tous les vendeurs</option>
                      {Object.entries(vendeursNoms).map(([em, nom]) => <option key={em} value={em}>{nom}</option>)}
                    </select>
                  )}
                  {courbeCat && (
                    <span className="text-[10px] text-ivory/45" data-testid="tax-courbe-cat-note">
                      (soumissions classées « {courbeCat} » seulement — commandes boutique non classées exclues)
                    </span>
                  )}
                  {courbeVendeur && (
                    <span className="text-[10px] text-ivory/45" data-testid="tax-courbe-vendeur-note">
                      (ventes de {vendeursNoms[courbeVendeur] || courbeVendeur} seulement — commandes boutique exclues)
                    </span>
                  )}
                </div>
                <svg viewBox="0 0 720 150" className="w-full" role="img"
                  aria-label="Courbe des ventes des 12 derniers mois — cliquez sur un mois pour voir son détail">
                  <line x1="24" y1="118" x2="696" y2="118" stroke="rgba(212,175,55,0.25)" strokeWidth="1" />
                  {/* Ligne d'OBJECTIF du mois (réglée dans Paramètres, demande du
                      propriétaire, 2026-10-22) : pointillés champagne + étiquette */}
                  {objectif > 0 && (
                    <g data-testid="tax-courbe-objectif">
                      <line x1="24" y1={courbeY(objectif)} x2="696" y2={courbeY(objectif)}
                        stroke="#f0d98c" strokeWidth="1.5" strokeDasharray="7 5" opacity="0.85" />
                      <text x="30" y={courbeY(objectif) - 5} textAnchor="start" fontSize="10"
                        fill="#f0d98c">Objectif {objectif.toFixed(0)} $</text>
                    </g>
                  )}
                  <polyline points={`${courbeX(0)},118 ${courbePts} ${courbeX(11)},118`}
                    fill="rgba(212,175,55,0.10)" stroke="none" />
                  <polyline points={courbePts} fill="none" stroke="#d4af37"
                    strokeWidth="2" strokeLinejoin="round" />
                  {courbe12.map((p, i) => {
                    // Objectif ATTEINT (demande du propriétaire, 2026-10-22) : le point
                    // d'un mois qui atteint ou dépasse l'objectif passe au VERT
                    const atteint = objectif > 0 && p.total >= objectif;
                    return (
                    <g key={p.cle} data-testid={`tax-courbe-point-${p.cle}`}
                      onClick={() => ouvrirMoisCourbe(p)} className="cursor-pointer">
                      <circle cx={courbeX(i)} cy={courbeY(p.total)} r={p.enCours ? 4 : 3}
                        fill={atteint ? "#34d399" : p.enCours ? "#f0d98c" : "#d4af37"}
                        data-testid={atteint ? `tax-courbe-atteint-${p.cle}` : undefined}>
                        <title>{`${p.label} ${p.cle.slice(0, 4)} : ${p.total.toFixed(2)} $${atteint ? " — objectif atteint" : ""} — cliquez pour le détail`}</title>
                      </circle>
                      {/* Zone de clic élargie (le point est petit) */}
                      <rect x={courbeX(i) - 20} y={10} width={40} height={120} fill="transparent">
                        <title>{`${p.label} ${p.cle.slice(0, 4)} : ${p.total.toFixed(2)} $ — cliquez pour le détail`}</title>
                      </rect>
                      <text x={courbeX(i)} y={136} textAnchor="middle" fontSize="10"
                        fill={atteint ? "#34d399" : p.enCours ? "#f0d98c" : "rgba(240,234,220,0.5)"}>
                        {p.label}
                      </text>
                      {p.enCours && (
                        <text x={courbeX(i)} y={148} textAnchor="middle" fontSize="8"
                          fill="rgba(240,217,140,0.7)">en cours</text>
                      )}
                    </g>
                    );
                  })}
                </svg>
              </div>
            )}
            {cumulRows.length === 0 ? (
              <p className="text-sm text-ivory/50" data-testid="tax-cumul-empty">Aucune vente confirmée pour cette période.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[680px]" data-testid="tax-cumul-table">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-widest text-gold border-b border-gold/20">
                      <th className={cell}>Période</th>
                      <th className={`${cellR} text-ivory/60`}>Ventes</th>
                      <th className={cellR}>Avant taxes</th>
                      <th className={cellR}>TPS</th>
                      <th className={cellR}>TVQ</th>
                      <th className={cellR}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cumulRows.map((g) => (
                      <tr key={g.periode} data-testid={`tax-cumul-row-${g.periode.replace(/\W+/g, "-")}`} className="border-b border-ivory/8">
                        <td className={cell}>{g.periode}</td>
                        <td className={`${cellR} text-ivory/60`}>{g.nb}</td>
                        <td className={cellR}>{money(g.avant_taxes)}</td>
                        <td className={cellR}>{money(g.tps)}</td>
                        <td className={cellR}>{money(g.tvq)}</td>
                        <td className={`${cellR} font-semibold`}>{money(g.total)}</td>
                        {/* Colonne Cumul RETIRÉE (demande du propriétaire, 2026-10-22) */}
                      </tr>
                    ))}
                    {/* Ligne TOTAL en bas de colonne (demande du propriétaire) :
                        somme de chaque colonne de la période affichée */}
                    {cumulRows.length > 0 && (
                      <tr className="font-bold border-t-2 border-gold/30" data-testid="tax-cumul-total-row">
                        <td className={cell}>Total</td>
                        <td className={`${cellR} text-ivory/60`} data-testid="tax-cumul-total-nb">{cumulRows.reduce((s, g) => s + g.nb, 0)}</td>
                        <td className={cellR} data-testid="tax-cumul-total-avant">{money(cumulRows.reduce((s, g) => s + g.avant_taxes, 0))}</td>
                        <td className={cellR} data-testid="tax-cumul-total-tps">{money(cumulRows.reduce((s, g) => s + g.tps, 0))}</td>
                        <td className={cellR} data-testid="tax-cumul-total-tvq">{money(cumulRows.reduce((s, g) => s + g.tvq, 0))}</td>
                        <td className={`${cellR} text-gold`} data-testid="tax-cumul-total-total">{money(cumulRows.reduce((s, g) => s + g.total, 0))}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Détail de la période cliquée : chaque vente avec ses taxes */}
          {detail && (
            <div className="mt-8" data-testid="tax-detail">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <p className="text-[11px] uppercase tracking-widest text-gold">
                  Détail — {detail.titre} ({detail.lignes.length} vente{detail.lignes.length > 1 ? "s" : ""})
                </p>
                {/* Période personnalisée (calendriers Du/Au) — EN PLUS du choix mois/trimestre */}
                <RangePicker idp="detail" from={dFrom} to={dTo} setFrom={setDFrom} setTo={setDTo} />
                {/* Imprime CE rapport (mois ou trimestre) — version papier pour le comptable */}
                <a href={detail.printUrl} target="_blank" rel="noreferrer" data-testid="tax-print-period"
                  title="Version imprimable de cette période (page blanche, prête pour le comptable)"
                  className="inline-flex items-center gap-1.5 border border-gold/40 text-gold px-4 py-1.5 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors duration-300">
                  <Printer className="w-3.5 h-3.5" /> Imprimer ce rapport
                </a>
              </div>
              {/* CUMULS de la plage choisie (demande du propriétaire) : le montant
                  cumulé de CHAQUE taxe et le montant total de la période */}
              <div className="flex flex-wrap gap-x-6 gap-y-1.5 mb-5 bg-gold/10 border border-gold/25 rounded-xl px-5 py-3"
                data-testid="tax-detail-cumul">
                <span className="text-sm text-ivory/80 font-semibold">Cumul de la période :</span>
                <span className="text-sm">Avant taxes <strong className="text-gold" data-testid="tax-cumul-avant">{money(detail.totaux.avant_taxes)}</strong></span>
                <span className="text-sm">TPS <strong className="text-gold" data-testid="tax-cumul-tps">{money(detail.totaux.tps)}</strong></span>
                <span className="text-sm">TVQ <strong className="text-gold" data-testid="tax-cumul-tvq">{money(detail.totaux.tvq)}</strong></span>
                {/* Total combiné des 2 taxes (demande du propriétaire) — montant
                    exact à remettre pour la période */}
                <span className="text-sm">TPS + TVQ <strong className="text-emerald-300" data-testid="tax-cumul-taxes2">{money(detail.totaux.tps + detail.totaux.tvq)}</strong></span>
                {detail.totaux.tvh > 0 && (
                  <span className="text-sm">TVH <strong className="text-gold" data-testid="tax-cumul-tvh">{money(detail.totaux.tvh)}</strong></span>
                )}
                <span className="text-sm">Total <strong className="text-gold" data-testid="tax-cumul-total">{money(detail.totaux.total)}</strong></span>
                {detail.lignes.some((v) => v.exempt) && (
                  <span className="text-sm text-emerald-300" data-testid="tax-cumul-exempt">
                    dont exemptées : <strong>{money(detail.lignes.filter((v) => v.exempt).reduce((s, v) => s + v.avant_taxes, 0))}</strong> avant taxes
                  </span>
                )}
              </div>
              {/* Chiffre d'affaires PAR JOUR (rapport mensuel, demande du propriétaire) */}
              {detail.jours && detail.jours.length > 0 && (
                <div className="overflow-x-auto mb-6">
                  <p className="text-[11px] uppercase tracking-widest text-ivory/50 mb-2">Chiffre d'affaires par jour</p>
                  <table className="w-full min-w-[520px]" data-testid="tax-daily-table">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-widest text-gold border-b border-gold/20">
                        <th className={cell}>Jour</th>
                        <th className={`${cellR} text-ivory/60`}>Ventes</th>
                        <th className={cellR}>Avant taxes</th>
                        <th className={cellR}>TPS</th>
                        <th className={cellR}>TVQ</th>
                        <th className={cellR}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.jours.map((j) => (
                        <tr key={j.jour} data-testid={`tax-day-${j.jour}`} className="border-b border-ivory/8">
                          <td className={cell}>{j.jour}</td>
                          <td className={`${cellR} text-ivory/60`}>{j.nb}</td>
                          <td className={cellR}>{money(j.avant_taxes)}</td>
                          <td className={cellR}>{money(j.tps)}</td>
                          <td className={cellR}>{money(j.tvq)}</td>
                          <td className={`${cellR} font-semibold`}>{money(j.total)}</td>
                        </tr>
                      ))}
                      {/* Ligne TOTAL du mois : cumul de chaque taxe + total (demande du propriétaire) */}
                      <tr className="font-bold border-t-2 border-gold/30" data-testid="tax-daily-total">
                        <td className={cell}>Total du mois</td>
                        <td className={`${cellR} text-ivory/60`}>{detail.lignes.length}</td>
                        <td className={cellR}>{money(detail.totaux.avant_taxes)}</td>
                        <td className={cellR}>{money(detail.totaux.tps)}</td>
                        <td className={cellR}>{money(detail.totaux.tvq)}</td>
                        <td className={`${cellR} font-semibold`}>{money(detail.totaux.total)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              {detail.lignes.length === 0 ? (
                <p className="text-sm text-ivory/50" data-testid="tax-detail-empty">Aucune vente confirmée sur cette période.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px]" data-testid="tax-detail-table">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-widest text-gold border-b border-gold/20">
                        <th className={cell}>Date</th>
                        <th className={cell}>Client</th>
                        <th className={cell}>Type</th>
                        <th className={cell}>Réf.</th>
                        <th className={cellR}>Avant taxes</th>
                        <th className={cellR}>TPS</th>
                        <th className={cellR}>TVQ</th>
                        <th className={cellR}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.lignes.map((v, i) => (
                        <tr key={`${v.ref}-${i}`} data-testid={`tax-vente-${v.ref}`} className="border-b border-ivory/8">
                          <td className={`${cell} text-ivory/60`}>{v.date}</td>
                          <td className={cell}>
                            {v.client}
                            {v.exempt && (
                              <span className="ml-1.5 text-[9px] uppercase tracking-widest border border-emerald-500/40 text-emerald-300 rounded-full px-1.5 py-0.5 align-middle"
                                data-testid={`tax-exempt-badge-${v.ref}`}>Exempt</span>
                            )}
                          </td>
                          <td className={cell}>
                            <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${
                              v.source === "Boutique" ? "border-sky-500/40 text-sky-300" : "border-gold/40 text-gold"}`}>
                              {v.source}
                              </span>
                          </td>
                          <td className={`${cell} text-ivory/50 text-xs`}>{v.ref}</td>
                          <td className={cellR}>{money(v.avant_taxes)}</td>
                          <td className={cellR}>{money(v.tps)}</td>
                          <td className={cellR}>{money(v.tvq)}</td>
                          <td className={`${cellR} font-semibold`}>{money(v.total)}</td>
                        </tr>
                      ))}
                      <tr className="text-gold font-bold" data-testid="tax-detail-total">
                        <td className={cell} colSpan={4}>Total de la période</td>
                        <td className={cellR}>{money(detail.totaux.avant_taxes)}</td>
                        <td className={cellR}>{money(detail.totaux.tps)}</td>
                        <td className={cellR}>{money(detail.totaux.tvq)}</td>
                        <td className={cellR}>{money(detail.totaux.total)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!loading && !data && (
        <p className="text-sm text-red-300" data-testid="tax-report-error">Rapport indisponible — réessayez.</p>
      )}

      <ShippingMarginCard />

      <MedalReportCard />

      {/* Ventes perdues — raisons (demande du propriétaire, 2026-10-06) */}
      <WithdrawalsCard />

      {/* Compilation des ventes TEST (formation) — tout en BAS de la page Rapports,
          réservée aux admins + code d'accès (demande du propriétaire, 2026-10-06) */}
      <TestSalesCard isAdmin={isAdmin} />

      {/* POPUP « Ventes du mois » — ouvert en cliquant un point de la courbe des
          12 mois (demande du propriétaire, 2026-10-23) : chaque vente confirmée
          du mois (soumissions + boutique), la plus récente en premier */}
      {moisVentes && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          role="dialog" aria-modal="true" data-testid="courbe-mois-modal"
          onClick={() => setMoisVentes(null)}>
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative bg-surface border border-gold/25 rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-ivory/10">
              <p className="text-sm font-bold text-champagne">
                Ventes de {moisVentes.label}
                {moisVentes.data && (
                  <span className="ml-2 text-xs font-normal text-ivory/60" data-testid="courbe-mois-total">
                    {moisVentes.data.nb} vente{moisVentes.data.nb > 1 ? "s" : ""} · {money(moisVentes.data.total)}
                  </span>
                )}
              </p>
              <button type="button" onClick={() => setMoisVentes(null)} data-testid="courbe-mois-close"
                className="text-ivory/60 hover:text-gold transition-colors text-lg leading-none px-2">×</button>
            </div>
            <div className="overflow-y-auto px-5 py-3">
              {!moisVentes.data ? (
                <p className="text-xs text-ivory/50 py-6 text-center">Chargement…</p>
              ) : moisVentes.data.ventes.length === 0 ? (
                <p className="text-xs text-ivory/50 py-6 text-center" data-testid="courbe-mois-vide">
                  Aucune vente confirmée ce mois-ci.
                </p>
              ) : (
                <ul className="divide-y divide-ivory/8" data-testid="courbe-mois-liste">
                  {moisVentes.data.ventes.map((v) => (
                    <li key={`${v.kind}-${v.id}`} data-testid={`courbe-mois-vente-${v.id}`}
                      className="py-2.5 flex items-center gap-3">
                      <span className={`shrink-0 text-[10px] uppercase tracking-wider rounded-full border px-2 py-0.5 ${
                        v.kind === "boutique" ? "border-sky-400/40 text-sky-300" : "border-gold/40 text-gold"}`}>
                        {v.kind === "boutique" ? "Boutique" : "Fiche"}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-ivory truncate">{v.client || "—"}</p>
                        <p className="text-[11px] text-ivory/50">{v.numero} · {v.date} · {v.articles} article{v.articles > 1 ? "s" : ""}</p>
                      </div>
                      <span className="shrink-0 text-sm font-semibold text-champagne">{money(v.total)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// Carte « Marge livraison » (demande du propriétaire) : prix FIXE payé par le
// client vs COÛTANT réel Stallion, par commande et en total. Le coûtant est
// visible ICI seulement — jamais au client.
function ShippingMarginCard() {
  const [days, setDays] = useState(90);
  const [m, setM] = useState(null);
  // Période personnalisée « Du … Au … » (calendriers, demande du propriétaire
  // 2026-10-06) — EN PLUS du choix de durée ; quand elle est active, on charge
  // jusqu'à 2 ans pour couvrir la période choisie
  const [mFrom, setMFrom] = useState("");
  const [mTo, setMTo] = useState("");
  useEffect(() => {
    let stop = false;
    const d = (mFrom || mTo) ? 730 : days;
    api.get(`/admin/reports/shipping-margin?days=${d}`)
      .then((r) => { if (!stop) setM(r.data); })
      .catch(() => { if (!stop) setM(null); });
    return () => { stop = true; };
  }, [days, mFrom, mTo]);
  const money = (v) => `${(v || 0).toFixed(2)} $`;
  const cell = "px-3 py-2.5 text-sm";
  const cellR = `${cell} text-right`;
  // Lignes et totaux filtrés par la période choisie (recalculés côté client)
  const cmds = (m?.commandes || []).filter((c) => inRange(c.date, mFrom, mTo));
  const totF = Math.round(cmds.reduce((s, c) => s + (c.facture || 0), 0) * 100) / 100;
  const totC = Math.round(cmds.reduce((s, c) => s + (c.coutant || 0), 0) * 100) / 100;
  const margeTot = Math.round((totF - totC) * 100) / 100;
  const sansEtiq = cmds.filter((c) => c.coutant == null).length;
  return (
    <div className="mt-10 border-t border-gold/15 pt-8" data-testid="shipping-margin">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-serif text-2xl">Marge livraison <span className="text-gold italic">Stallion</span></h3>
          <p className="text-xs text-ivory/55 mt-1">Prix fixe payé par le client vs coûtant réel du transporteur (coûtant visible ici seulement).</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Période personnalisée (calendriers Du/Au) — EN PLUS du choix de durée */}
          <RangePicker idp="margin" from={mFrom} to={mTo} setFrom={setMFrom} setTo={setMTo} />
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="margin-days"
            className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm [color-scheme:dark]">
            <option value={30}>30 derniers jours</option>
            <option value={90}>90 derniers jours</option>
            <option value={365}>12 derniers mois</option>
          </select>
        </div>
      </div>
      {!m ? (
        <p className="text-sm text-ivory/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              ["Facturé aux clients", totF, "text-ivory"],
              ["Coûtant Stallion", totC, "text-ivory"],
              ["Marge livraison", margeTot, margeTot >= 0 ? "text-emerald-300" : "text-red-300"],
            ].map(([lab, val, cls]) => (
              <div key={lab} className="bg-ink border border-ivory/10 rounded-xl px-4 py-3">
                <p className="text-[10px] uppercase tracking-widest text-ivory/50">{lab}</p>
                <p className={`font-serif text-2xl mt-1 ${cls}`} data-testid={`margin-${lab.includes("Facturé") ? "facture" : lab.includes("Coûtant") ? "coutant" : "marge"}`}>{money(val)}</p>
              </div>
            ))}
            <div className="bg-ink border border-ivory/10 rounded-xl px-4 py-3">
              <p className="text-[10px] uppercase tracking-widest text-ivory/50">Colis sans étiquette</p>
              <p className="font-serif text-2xl mt-1 text-champagne" data-testid="margin-sans-etiquette">{sansEtiq}</p>
            </div>
          </div>
          {cmds.length === 0 ? (
            <p className="text-sm text-ivory/50" data-testid="margin-empty">Aucune livraison postale sur la période.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]" data-testid="margin-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-gold border-b border-gold/20">
                    <th className={cell}>Date</th>
                    <th className={cell}>Commande</th>
                    <th className={cell}>Client</th>
                    <th className={cell}>Service</th>
                    <th className={cellR}>Facturé</th>
                    <th className={cellR}>Coûtant</th>
                    <th className={cellR}>Marge</th>
                  </tr>
                </thead>
                <tbody>
                  {cmds.map((c) => (
                    <tr key={c.order_number} data-testid={`margin-row-${c.order_number}`} className="border-b border-ivory/8">
                      <td className={`${cell} text-ivory/60`}>{c.date}</td>
                      <td className={`${cell} text-ivory/80`}>{c.order_number}</td>
                      <td className={cell}>{c.client}</td>
                      <td className={`${cell} text-ivory/50 text-xs`}>{c.service || "—"}</td>
                      <td className={cellR}>{money(c.facture)}</td>
                      <td className={cellR}>{c.coutant != null ? money(c.coutant) : <span className="text-ivory/40 text-xs">sans étiquette</span>}</td>
                      <td className={`${cellR} font-semibold ${c.marge == null ? "text-ivory/40" : c.marge >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                        {c.marge != null ? money(c.marge) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}



// Carte « Ventes de médailles » (demande du propriétaire) : mini-rapport des
// médailles d'animaux vendues — PAR MODÈLE et PAR COULEUR, commandes boutique
// payées seulement (ventes test exclues).
function MedalReportCard() {
  const [m, setM] = useState(null);
  const [err, setErr] = useState(false);
  // Période « Du … Au … » (demande du propriétaire, 2026-10-06)
  const [mFrom, setMFrom] = useState("");
  const [mTo, setMTo] = useState("");
  useEffect(() => {
    let stop = false;
    api.get(`/admin/reports/medals?date_from=${mFrom}&date_to=${mTo}`)
      .then((r) => { if (!stop) setM(r.data); })
      .catch(() => { if (!stop) setErr(true); });
    return () => { stop = true; };
  }, [mFrom, mTo]);
  const money = (v) => `${(v || 0).toFixed(2)} $`;
  const cell = "px-3 py-2.5 text-sm";
  const cellR = `${cell} text-right`;
  return (
    <div className="mt-10 border-t border-gold/15 pt-8" data-testid="medal-report">
      <div className="mb-5">
        <h3 className="font-serif text-2xl">Médailles <span className="text-gold italic">chats - chiens - autres</span></h3>
        <p className="text-xs text-ivory/55 mt-1">Ventes par modèle et par couleur — commandes boutique payées.</p>
        <div className="mt-3">
          <RangePicker from={mFrom} to={mTo} setFrom={setMFrom} setTo={setMTo} idp="medals" />
        </div>
      </div>
      {err ? (
        <p className="text-sm text-red-300" data-testid="medal-report-error">Rapport indisponible — réessayez.</p>
      ) : !m ? (
        <p className="text-sm text-ivory/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            <div className="bg-ink border border-ivory/10 rounded-xl px-4 py-3">
              <p className="text-[10px] uppercase tracking-widest text-ivory/50">Médailles vendues</p>
              <p className="font-serif text-2xl mt-1 text-ivory" data-testid="medal-total-qty">{m.total_qty}</p>
            </div>
            <div className="bg-ink border border-ivory/10 rounded-xl px-4 py-3">
              <p className="text-[10px] uppercase tracking-widest text-ivory/50">Revenu médailles</p>
              <p className="font-serif text-2xl mt-1 text-gold" data-testid="medal-total-revenu">{money(m.total_revenu)}</p>
            </div>
            <div className="bg-ink border border-ivory/10 rounded-xl px-4 py-3">
              <p className="text-[10px] uppercase tracking-widest text-ivory/50">Commandes avec médaille</p>
              <p className="font-serif text-2xl mt-1 text-champagne" data-testid="medal-orders">{m.nb_commandes}</p>
            </div>
          </div>
          {m.par_item.length === 0 ? (
            <p className="text-sm text-ivory/50" data-testid="medal-empty">Aucune médaille vendue pour le moment.</p>
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="medal-models-table">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-widest text-gold border-b border-gold/20">
                        <th className={cell}>Modèle</th>
                        <th className={cellR}>Qté</th>
                        <th className={cellR}>Revenu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.par_modele.map((r) => (
                        <tr key={r.label} data-testid={`medal-model-${r.label}`} className="border-b border-ivory/8">
                          <td className={cell}>{r.label}</td>
                          <td className={cellR}>{r.qty}</td>
                          <td className={`${cellR} text-champagne`}>{money(r.revenu)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="medal-colors-table">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-widest text-gold border-b border-gold/20">
                        <th className={cell}>Couleur</th>
                        <th className={cellR}>Qté</th>
                        <th className={cellR}>Revenu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.par_couleur.map((r) => (
                        <tr key={r.label} data-testid={`medal-color-${r.label}`} className="border-b border-ivory/8">
                          <td className={cell}>{r.label}</td>
                          <td className={cellR}>{r.qty}</td>
                          <td className={`${cellR} text-champagne`}>{money(r.revenu)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px]" data-testid="medal-items-table">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-widest text-gold border-b border-gold/20">
                      <th className={cell}>Code</th>
                      <th className={cell}>Médaille</th>
                      <th className={cell}>Couleur</th>
                      <th className={cellR}>Qté</th>
                      <th className={cellR}>Revenu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.par_item.map((r) => (
                      <tr key={r.sku || r.name} data-testid={`medal-row-${r.sku || r.name}`} className="border-b border-ivory/8">
                        <td className={`${cell} text-ivory/60 text-xs uppercase`}>{r.sku || "—"}</td>
                        <td className={cell}>{r.name}</td>
                        <td className={`${cell} text-ivory/70`}>{r.couleur}</td>
                        <td className={cellR}>{r.qty}</td>
                        <td className={`${cellR} text-champagne`}>{money(r.revenu)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

    </div>
  );
}